import type { Extended } from "./types";
import { merge, type Rows } from "./store";

/**
 * WHAT IS WAITING TO BE SAVED.
 *
 * Every keystroke changes a setting. Sending each one is a request per letter;
 * sending nothing until the organiser thinks to press a button is how an
 * afternoon's work disappears. So changes ACCUMULATE and leave in batches.
 *
 * Kept pure — no timer, no network, no React — because the parts that go wrong
 * are the bookkeeping ones: two edits to the same key, edits in two languages at
 * once, and above all A FAILED SEND THAT MUST NOT LOSE ANYTHING. Those are
 * settled by tests; the timer that drives it is trivial by comparison.
 *
 * ⚠️ ONLY WHAT CHANGED is queued, never the whole object. The server deep-merges
 * (`updateFo`), so a partial payload is the correct one — and it means two people
 * editing different settings do not overwrite each other.
 */

/** Pending changes, by language id. */
export type Pending = Record<number, Extended>;

/**
 * Keys to REMOVE, by language id. Held apart from the values because they are a
 * different instruction: a merge cannot express a removal, so the route takes
 * them as their own list.
 */
export type PendingUnset = Record<number, string[]>;

/**
 * ⚠️ `refused` EST À PART DE `failed`, et ce n'est pas un détail de vocabulaire.
 * « Échec » dit à l'organisateur que rien n'est perdu et qu'une nouvelle tentative
 * aura lieu — vrai d'un réseau coupé, FAUX d'un 401 : le serveur refusera la
 * millième fois comme la première. Les confondre, c'est promettre un
 * enregistrement qui n'arrivera jamais, et laisser quelqu'un remplir des écrans
 * pour rien.
 */
export type SyncState =
  | "idle"
  | "waiting"
  | "saving"
  | "saved"
  | "failed"
  | "refused";

export function queue(
  pending: Pending,
  languageId: number,
  extended: Extended,
): Pending {
  return {
    ...pending,
    // The same deep merge the server performs, so what we hold is exactly what
    // it will end up with — including `typosV2` merged per typology.
    [languageId]: merge(pending[languageId] ?? {}, extended),
  };
}

export function queueUnset(
  pending: PendingUnset,
  languageId: number,
  paths: string[],
): PendingUnset {
  const before = pending[languageId] ?? [];
  // Deduplicated: clearing the same override twice before a send must not ask
  // the server to remove it twice.
  return { ...pending, [languageId]: [...new Set([...before, ...paths])] };
}

/**
 * ═══ VALEUR ET EFFACEMENT SUR LA MÊME CLÉ SE RÉPONDENT ═══
 *
 * ⚠️ `record` (valeurs) et `forget` (effacements) alimentent deux files. Sans se parler,
 * un lot pouvait porter `extended: {A: "y"}` ET `unset: ["A"]` : le serveur fusionne A
 * puis le supprime, et la valeur re-saisie disparaît au rechargement pendant que le
 * panneau affiche « Enregistré ». La règle est « le dernier geste gagne » — écrire une
 * valeur annule un effacement en attente de la même clé, et l'inverse.
 *
 * `cheminsDe` traduit un objet de réglages en chemins pointés — le même vocabulaire que
 * les `unset` (`writePath`/`clearPath`) — pour pouvoir annuler l'un avec l'autre :
 * `{ typosV2: { "3": { A: 1 } }, B: 2 }` → `["typosV2.3.A", "B"]`.
 */
export function pathsOf(extended: Extended, prefix = ""): string[] {
  const paths: string[] = [];
  for (const [key, value] of Object.entries(extended)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      paths.push(...pathsOf(value as Extended, path));
    } else {
      paths.push(path);
    }
  }
  return paths;
}

/** Retire des chemins de la file d'effacement d'une langue (une valeur les remplace). */
export function purgeUnset(
  pending: PendingUnset,
  languageId: number,
  paths: string[],
): PendingUnset {
  const current = pending[languageId];
  if (!current || paths.length === 0) return pending;
  const toRemove = new Set(paths);
  const remaining = current.filter((p) => !toRemove.has(p));
  if (remaining.length === current.length) return pending;
  const out = { ...pending };
  if (remaining.length === 0) delete out[languageId];
  else out[languageId] = remaining;
  return out;
}

/** Retire un chemin pointé d'un objet de réglages, en purgeant les objets devenus vides. */
function withoutPath(object: Extended, parts: string[]): Extended {
  const [head, ...remaining] = parts;
  if (head === undefined || !(head in object)) return object;
  const copy: Extended = { ...object };
  if (remaining.length === 0) {
    delete copy[head];
    return copy;
  }
  const child = object[head];
  if (!child || typeof child !== "object" || Array.isArray(child)) return object;
  const cleaned = withoutPath(child as Extended, remaining);
  if (Object.keys(cleaned).length === 0) delete copy[head];
  else copy[head] = cleaned;
  return copy;
}

/** Retire des chemins de la file de valeurs d'une langue (un effacement les annule). */
export function purgePending(
  pending: Pending,
  languageId: number,
  paths: string[],
): Pending {
  const current = pending[languageId];
  if (!current || paths.length === 0) return pending;
  let cleaned = current;
  for (const p of paths) cleaned = withoutPath(cleaned, p.split("."));
  if (cleaned === current) return pending;
  const out = { ...pending };
  if (Object.keys(cleaned).length === 0) delete out[languageId];
  else out[languageId] = cleaned;
  return out;
}

export function isEmpty(pending: Pending, unset: PendingUnset = {}): boolean {
  return Object.keys(pending).length === 0 && Object.keys(unset).length === 0;
}

/** One send per language: the route takes a single `languageId` at a time. */
export function batches(
  pending: Pending,
  unset: PendingUnset = {},
): Array<{ languageId: number; extended: Extended; unset: string[] }> {
  // Every language mentioned by either side gets one batch: a language whose
  // only change is a removal must still be sent.
  const ids = new Set([...Object.keys(pending), ...Object.keys(unset)]);
  return [...ids].map((id) => ({
    languageId: Number(id),
    extended: pending[Number(id)] ?? {},
    unset: unset[Number(id)] ?? [],
  }));
}

/**
 * Puts a failed batch back at the FRONT of what is still pending.
 *
 * The order matters and is easy to get backwards: while a send was in flight the
 * organiser kept typing, so `pending` holds NEWER values. Merging the failed
 * batch on top would resurrect what they have since changed. The retry goes
 * underneath — it only fills in keys nobody has touched again.
 */
export function restoreUnset(
  pending: PendingUnset,
  failed: PendingUnset,
): PendingUnset {
  const out: PendingUnset = { ...pending };
  for (const [id, paths] of Object.entries(failed)) {
    const languageId = Number(id);
    out[languageId] = [...new Set([...paths, ...(out[languageId] ?? [])])];
  }
  return out;
}

export function restore(pending: Pending, failed: Pending): Pending {
  const out: Pending = { ...pending };
  for (const [id, extended] of Object.entries(failed)) {
    const languageId = Number(id);
    out[languageId] = merge(extended, out[languageId] ?? {});
  }
  return out;
}

/**
 * ═══ CE QUI REDEVIENT ÉGAL AU SERVEUR NE PART PAS ═══
 *
 * ⚠️ REVENIR SUR UN GESTE DOIT LE DÉFAIRE, PAS EN AJOUTER UN. Cocher « English » puis le
 * décocher laissait la valeur dans la file : « Enregistrer » restait allumé, le départ de
 * la page demandait confirmation, et rien n'avait changé (#295). La file se compare donc,
 * à chaque geste, à ce que le serveur tient : une valeur qui lui redevient égale en sort,
 * un effacement d'une clé qu'il n'a pas aussi.
 *
 * ⚠️ COMPARÉ PAR UNITÉ DE FUSION, jamais feuille par feuille : une clé entière, ou une clé
 * d'une typologie (`typosV2.3.A`), comme `merge` les remplace. Élaguer à l'intérieur d'une
 * valeur objet aurait envoyé une valeur tronquée, qui remplace l'entière.
 */
export function withoutNoOps(
  pending: Pending,
  unset: PendingUnset,
  saved: Rows,
): { pending: Pending; unset: PendingUnset } {
  const keptPending: Pending = {};
  for (const [id, extended] of Object.entries(pending)) {
    const languageId = Number(id);
    let cleaned = extended;
    for (const unit of unitsOf(extended)) {
      if (same(valueAt(extended, unit), valueAt(saved[languageId], unit))) {
        cleaned = withoutPath(cleaned, unit.split("."));
      }
    }
    if (Object.keys(cleaned).length > 0) keptPending[languageId] = cleaned;
  }

  const keptUnset: PendingUnset = {};
  for (const [id, paths] of Object.entries(unset)) {
    const languageId = Number(id);
    const remaining = paths.filter(
      (p) => valueAt(saved[languageId], p) !== undefined,
    );
    if (remaining.length > 0) keptUnset[languageId] = remaining;
  }

  return { pending: keptPending, unset: keptUnset };
}

/**
 * Ce que le serveur tient une fois un lot accepté : la même fusion que la sienne, puis
 * les effacements. C'est la référence de `withoutNoOps` pour les gestes suivants.
 */
export function settle(
  saved: Rows,
  pending: Pending,
  unset: PendingUnset = {},
): Rows {
  const out: Rows = { ...saved };
  for (const [id, extended] of Object.entries(pending)) {
    out[Number(id)] = merge(out[Number(id)] ?? {}, extended);
  }
  for (const [id, paths] of Object.entries(unset)) {
    let row = out[Number(id)];
    if (!row) continue;
    for (const p of paths) row = withoutPath(row, p.split("."));
    out[Number(id)] = row;
  }
  return out;
}

/** Les unités que `merge` remplace d'un bloc : une clé, ou une clé d'une typologie. */
function unitsOf(extended: Extended): string[] {
  const units: string[] = [];
  for (const [key, value] of Object.entries(extended)) {
    if (key === "typosV2" && value && typeof value === "object") {
      for (const [typoId, keys] of Object.entries(
        value as Record<string, Extended | undefined>,
      )) {
        for (const k of Object.keys(keys ?? {}))
          units.push(`typosV2.${typoId}.${k}`);
      }
    } else {
      units.push(key);
    }
  }
  return units;
}

/** La valeur rangée sous un chemin pointé, `undefined` quand il n'existe pas. */
function valueAt(extended: Extended | undefined, path: string): unknown {
  let node: unknown = extended;
  for (const part of path.split(".")) {
    if (!node || typeof node !== "object" || Array.isArray(node))
      return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** Égalité profonde de deux valeurs de réglage (JSON : scalaires, listes, objets). */
function same(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every(
      (k) =>
        Object.prototype.hasOwnProperty.call(right, k) &&
        same(left[k], right[k]),
    )
  );
}
